import type { IconType } from "react-icons";
import {
  FaBaseballBall,
  FaBasketballBall,
  FaBicycle,
  FaDumbbell,
  FaFutbol,
  FaGolfBall,
  FaHockeyPuck,
  FaRunning,
  FaSwimmer,
  FaTableTennis,
  FaVolleyballBall,
} from "react-icons/fa";
import {
  GiAmericanFootballBall,
  GiBoxingGlove,
  GiCricketBat,
  GiShuttlecock,
  GiTennisRacket,
} from "react-icons/gi";

type SportVisual = {
  accent: string;
  glow: string;
  Icon: IconType;
};

type SportIconRule = {
  pattern: RegExp;
  visual: SportVisual;
};

const defaultSportVisual: SportVisual = {
  accent: "#B27A17",
  glow: "rgba(178, 122, 23, 0.28)",
  Icon: FaDumbbell,
};

const sportIconRules: SportIconRule[] = [
  {
    pattern: /(american football|gridiron|nfl)/i,
    visual: {
      accent: "#9A3412",
      glow: "rgba(154, 52, 18, 0.28)",
      Icon: GiAmericanFootballBall,
    },
  },
  {
    pattern: /(football|soccer|futbol)/i,
    visual: {
      accent: "#15803D",
      glow: "rgba(21, 128, 61, 0.28)",
      Icon: FaFutbol,
    },
  },
  {
    pattern: /basketball/i,
    visual: {
      accent: "#C2410C",
      glow: "rgba(194, 65, 12, 0.28)",
      Icon: FaBasketballBall,
    },
  },
  {
    pattern: /(baseball|softball)/i,
    visual: {
      accent: "#B91C1C",
      glow: "rgba(185, 28, 28, 0.28)",
      Icon: FaBaseballBall,
    },
  },
  {
    pattern: /cricket/i,
    visual: {
      accent: "#0F766E",
      glow: "rgba(15, 118, 110, 0.28)",
      Icon: GiCricketBat,
    },
  },
  {
    pattern: /rugby/i,
    visual: {
      accent: "#166534",
      glow: "rgba(22, 101, 52, 0.28)",
      Icon: GiAmericanFootballBall,
    },
  },
  {
    pattern: /tennis/i,
    visual: {
      accent: "#65A30D",
      glow: "rgba(101, 163, 13, 0.28)",
      Icon: GiTennisRacket,
    },
  },
  {
    pattern: /(table tennis|ping pong)/i,
    visual: {
      accent: "#0284C7",
      glow: "rgba(2, 132, 199, 0.28)",
      Icon: FaTableTennis,
    },
  },
  {
    pattern: /(volleyball|netball)/i,
    visual: {
      accent: "#7C3AED",
      glow: "rgba(124, 58, 237, 0.28)",
      Icon: FaVolleyballBall,
    },
  },
  {
    pattern: /(athletics|track|running|marathon)/i,
    visual: {
      accent: "#2563EB",
      glow: "rgba(37, 99, 235, 0.28)",
      Icon: FaRunning,
    },
  },
  {
    pattern: /swimming/i,
    visual: {
      accent: "#0EA5E9",
      glow: "rgba(14, 165, 233, 0.28)",
      Icon: FaSwimmer,
    },
  },
  {
    pattern: /cycling/i,
    visual: {
      accent: "#4F46E5",
      glow: "rgba(79, 70, 229, 0.28)",
      Icon: FaBicycle,
    },
  },
  {
    pattern: /golf/i,
    visual: {
      accent: "#65A30D",
      glow: "rgba(101, 163, 13, 0.28)",
      Icon: FaGolfBall,
    },
  },
  {
    pattern: /hockey/i,
    visual: {
      accent: "#1D4ED8",
      glow: "rgba(29, 78, 216, 0.28)",
      Icon: FaHockeyPuck,
    },
  },
  {
    pattern: /badminton/i,
    visual: {
      accent: "#0891B2",
      glow: "rgba(8, 145, 178, 0.28)",
      Icon: GiShuttlecock,
    },
  },
  {
    pattern: /(boxing|combat)/i,
    visual: {
      accent: "#DC2626",
      glow: "rgba(220, 38, 38, 0.28)",
      Icon: GiBoxingGlove,
    },
  },
];

export const getSportVisual = (sportName?: string | null): SportVisual => {
  if (!sportName) {
    return defaultSportVisual;
  }

  return sportIconRules.find(({ pattern }) => pattern.test(sportName))?.visual || defaultSportVisual;
};
